import type React from "react";

import { useState } from "react";
import { useTranslation } from "../../../i18n";
import {
  ChevronRight,
  CreditCard,
  FileText,
  DollarSign,
  Calendar,
  Building,
  RefreshCw,
  Eye,
  CheckCircle,
} from "lucide-react";

interface FeatureDetail {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
  benefits: string[];
}

export default function BillingFeaturesDetail() {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState("payment-capture");

  const features: FeatureDetail[] = [
    {
      id: "payment-capture",
      title: t('routes.billing.featuresDetail.features.0.title'),
      description: t('routes.billing.featuresDetail.features.0.description'),
      icon: <CreditCard className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.0.details.0'),
        t('routes.billing.featuresDetail.features.0.details.1'),
        t('routes.billing.featuresDetail.features.0.details.2'),
        t('routes.billing.featuresDetail.features.0.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.0.benefits.0'),
        t('routes.billing.featuresDetail.features.0.benefits.1'),
        t('routes.billing.featuresDetail.features.0.benefits.2'),
      ],
    },
    {
      id: "partial-auth",
      title: t('routes.billing.featuresDetail.features.1.title'),
      description: t('routes.billing.featuresDetail.features.1.description'),
      icon: <DollarSign className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.1.details.0'),
        t('routes.billing.featuresDetail.features.1.details.1'),
        t('routes.billing.featuresDetail.features.1.details.2'),
        t('routes.billing.featuresDetail.features.1.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.1.benefits.0'),
        t('routes.billing.featuresDetail.features.1.benefits.1'),
        t('routes.billing.featuresDetail.features.1.benefits.2'),
      ],
    },
    {
      id: "automated-invoicing",
      title: t('routes.billing.featuresDetail.features.2.title'),
      description: t('routes.billing.featuresDetail.features.2.description'),
      icon: <FileText className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.2.details.0'),
        t('routes.billing.featuresDetail.features.2.details.1'),
        t('routes.billing.featuresDetail.features.2.details.2'),
        t('routes.billing.featuresDetail.features.2.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.2.benefits.0'),
        t('routes.billing.featuresDetail.features.2.benefits.1'),
        t('routes.billing.featuresDetail.features.2.benefits.2'),
      ],
    },
    {
      id: "monthly-payouts",
      title: t('routes.billing.featuresDetail.features.3.title'),
      description: t('routes.billing.featuresDetail.features.3.description'),
      icon: <Calendar className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.3.details.0'),
        t('routes.billing.featuresDetail.features.3.details.1'),
        t('routes.billing.featuresDetail.features.3.details.2'),
        t('routes.billing.featuresDetail.features.3.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.3.benefits.0'),
        t('routes.billing.featuresDetail.features.3.benefits.1'),
        t('routes.billing.featuresDetail.features.3.benefits.2'),
      ],
    },
    {
      id: "accounting-areas",
      title: t('routes.billing.featuresDetail.features.4.title'),
      description: t('routes.billing.featuresDetail.features.4.description'),
      icon: <Building className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.4.details.0'),
        t('routes.billing.featuresDetail.features.4.details.1'),
        t('routes.billing.featuresDetail.features.4.details.2'),
        t('routes.billing.featuresDetail.features.4.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.4.benefits.0'),
        t('routes.billing.featuresDetail.features.4.benefits.1'),
        t('routes.billing.featuresDetail.features.4.benefits.2'),
      ],
    },
    {
      id: "self-service-refunds",
      title: t('routes.billing.featuresDetail.features.5.title'),
      description: t('routes.billing.featuresDetail.features.5.description'),
      icon: <RefreshCw className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.5.details.0'),
        t('routes.billing.featuresDetail.features.5.details.1'),
        t('routes.billing.featuresDetail.features.5.details.2'),
        t('routes.billing.featuresDetail.features.5.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.5.benefits.0'),
        t('routes.billing.featuresDetail.features.5.benefits.1'),
        t('routes.billing.featuresDetail.features.5.benefits.2'),
      ],
    },
    {
      id: "real-time-view",
      title: t('routes.billing.featuresDetail.features.6.title'),
      description: t('routes.billing.featuresDetail.features.6.description'),
      icon: <Eye className="h-6 w-6" />,
      details: [
        t('routes.billing.featuresDetail.features.6.details.0'),
        t('routes.billing.featuresDetail.features.6.details.1'),
        t('routes.billing.featuresDetail.features.6.details.2'),
        t('routes.billing.featuresDetail.features.6.details.3'),
      ],
      benefits: [
        t('routes.billing.featuresDetail.features.6.benefits.0'),
        t('routes.billing.featuresDetail.features.6.benefits.1'),
        t('routes.billing.featuresDetail.features.6.benefits.2'),
      ],
    },
  ];

  const activeFeatureData =
    features.find((f) => f.id === activeFeature) || features[0];

  return (
    <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('routes.billing.featuresDetail.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('routes.billing.featuresDetail.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feature Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('routes.billing.featuresDetail.navTitle')}
              </h3>
              <nav className="space-y-2">
                {features.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => setActiveFeature(feature.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 flex items-center ${
                      activeFeature === feature.id
                        ? "bg-green-100 text-green-800 border-l-4 border-green-500"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <div
                      className={`mr-3 ${
                        activeFeature === feature.id
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{feature.title}</div>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        activeFeature === feature.id ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Feature Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                <div className="flex items-center mb-4">
                  <div className="mr-4 p-2 bg-white/20 rounded-lg">
                    {activeFeatureData.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">
                      {activeFeatureData.title}
                    </h3>
                    <p className="text-green-100 mt-1">
                      {activeFeatureData.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Details */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('routes.billing.featuresDetail.howItWorks')}
                    </h4>
                    <ul className="space-y-3">
                      {activeFeatureData.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('routes.billing.featuresDetail.keyBenefits')}
                    </h4>
                    <ul className="space-y-3">
                      {activeFeatureData.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <div className="h-5 w-5 bg-green-500 rounded-full mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center">
                            <CheckCircle className="h-3 w-3 text-white" />
                          </div>
                          <span className="text-gray-600 font-medium">
                            {benefit}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
